import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Activity } from 'lucide-react';
import { HighGuardBadge } from './HighGuardBadge';

const MultiCoinStatus = ({
  pairsData,
  bleedPairs,
  bleedLoading,
  dumpConfirm,
  setDumpConfirm,
  formatPrice,
  handleToggleBleed,
  handleDumpCoin,
  setActiveTab,
}) => {
  if (!Object.keys(pairsData).length) return null;

  return (
    <Card className="matrix-card rounded-none" data-testid="multi-coin-card">
      <CardHeader className="border-b border-[#003B00] p-3">
        <CardTitle className="text-sm uppercase tracking-widest flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4" strokeWidth={1.5} />
            MULTI-COIN STATUS
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-xs border-cyan-500 text-cyan-400 hover:bg-cyan-600 hover:text-white"
            onClick={() => {
              const chartEl = document.querySelector('[data-testid="multi-coin-section"]');
              if (chartEl) chartEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            data-testid="view-multi-chart-btn"
          >
            MULTI-CHART
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          {Object.entries(pairsData).map(([pair, data]) => (
            <div key={pair} className="border border-[#003B00] p-3 rounded">
              {bleedPairs.includes(pair) && (
                <div className="bg-orange-900/30 border border-orange-500/50 rounded px-2 py-1 mb-2 text-xs text-orange-400 flex items-center gap-1">
                  <span className="animate-pulse">●</span> BLEED MODE: Sell only, no new buys
                </div>
              )}
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-[#00FF41]">{pair.split('-')[0]}</span>
                <div className="flex items-center gap-2">
                  <span className="text-cyan-400">{formatPrice(data?.price || 0)}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className={`h-6 px-2 text-xs ${
                      bleedPairs.includes(pair)
                        ? 'bg-orange-600 border-orange-600 text-white hover:bg-orange-700'
                        : 'border-orange-500 text-orange-400 hover:bg-orange-600 hover:text-white'
                    }`}
                    onClick={() => handleToggleBleed(pair)}
                    disabled={bleedLoading === pair}
                    data-testid={`bleed-btn-${pair}`}
                    title={bleedPairs.includes(pair) ? 'Click to resume normal trading' : 'Enable sell-only mode (no more buys)'}
                  >
                    {bleedLoading === pair ? '...' : 'BLEED'}
                  </Button>
                  {(data?.balance || 0) > 0 && (
                    dumpConfirm.open && dumpConfirm.pair === pair ? (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-6 px-2 text-xs bg-red-600 hover:bg-red-700"
                          onClick={() => handleDumpCoin(pair)}
                          data-testid={`dump-confirm-yes-${pair}`}
                        >
                          YES
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs border-[#003B00] hover:bg-[#001a00]"
                          onClick={() => setDumpConfirm({ open: false, pair: null, coinName: null })}
                          data-testid={`dump-confirm-no-${pair}`}
                        >
                          NO
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                        onClick={() => setDumpConfirm({ open: true, pair, coinName: pair.split('-')[0] })}
                        data-testid={`dump-btn-${pair}`}
                      >
                        DUMP
                      </Button>
                    )
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-[#008F11]">BALANCE: </span>
                  <span className="text-[#00FF41]">{(data?.balance || 0).toFixed(6)}</span>
                </div>
                <div>
                  <span className="text-[#008F11]">VALUE: </span>
                  <span className="text-[#00FF41]">${(data?.value || 0).toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-[#008F11]">RSI: </span>
                  <span className={`font-bold ${
                    (data?.rsi ?? 50) <= 30 ? 'text-[#00FF41]' : 
                    (data?.rsi ?? 50) >= 70 ? 'text-red-500' : 
                    'text-yellow-500'
                  }`}>
                    {(data?.rsi ?? 50).toFixed(1)}
                    <span className="text-xs ml-1">
                      {(data?.rsi ?? 50) <= 30 ? '(BUY)' : (data?.rsi ?? 50) >= 70 ? '(SELL)' : '(HOLD)'}
                    </span>
                  </span>
                </div>
                <div>
                  <span className="text-[#008F11]">AVG BUY: </span>
                  {(data?.avg_buy_price || 0) > 0 && (data?.balance || 0) > 0.0000001 ? (
                    <span className="text-cyan-400">${formatPrice(data.avg_buy_price, pair)}</span>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </div>
                {(data?.min_sell_price || 0) > 0 && (
                  <div>
                    <span className="text-[#008F11]">MIN SELL: </span>
                    <span className={`font-bold ${
                      (data?.price || 0) >= data.min_sell_price ? 'text-green-400' : 'text-orange-400'
                    }`}>
                      ${formatPrice(data.min_sell_price, pair)}
                      {(data?.price || 0) >= data.min_sell_price 
                        ? <span className="text-xs ml-1 text-green-400">(READY)</span>
                        : <span className="text-xs ml-1 text-orange-400">(BELOW)</span>
                      }
                    </span>
                  </div>
                )}
                {(data?.balance || 0) > 0.0000001 && (data?.avg_buy_price || 0) > 0 && (
                  <div>
                    <span className="text-[#008F11]">DUMP P/L: </span>
                    <span className={`font-bold ${
                      ((data?.price || 0) * 0.9925 - (data?.avg_buy_price || 0)) * (data?.balance || 0) >= 0 
                        ? 'text-green-500' 
                        : 'text-red-500'
                    }`}>
                      {((data?.price || 0) * 0.9925 - (data?.avg_buy_price || 0)) * (data?.balance || 0) >= 0 ? '+' : ''}
                      ${(((data?.price || 0) * 0.9925 - (data?.avg_buy_price || 0)) * (data?.balance || 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
              <HighGuardBadge pair={pair} guard={data?.high_guard} price={data?.price || 0} formatPrice={formatPrice} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default MultiCoinStatus;
